import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { toSupportTicket } from '@/shared/api/adapt'
import { api } from '@/shared/api/client'
import type {
  AppConnect,
  AppConnected,
  AppStatus,
  LeaderRead,
  LeadersUpdate,
  ManualStructure,
  Page,
  ReporterSummary,
  SupportAppRead,
  TicketItem,
  TicketUpdate,
} from '@/shared/api/contracts'
import { qk } from '@/shared/api/query-keys'
import { errorMessage } from '@/shared/components/data'
import { ensureUserIndex } from '@/shared/hooks/use-users'
import { useT } from '@/shared/i18n'
import { toSearchParams } from '@/shared/lib/query-params'
import type { SupportLeader, SupportReporter, SupportTicket } from '@/shared/types/domain'

import { toSupportQuery, type SupportFilters } from './use-support-filters'

/**
 * Los issues reportados, **en el orden del servidor**: urgentes primero y, en
 * cada severidad, del más nuevo al más viejo. La pantalla no reordena.
 */
export function useSupportTickets(filters: SupportFilters) {
  const client = useQueryClient()
  const query = toSupportQuery(filters)

  return useQuery({
    queryKey: qk.support.tickets(query),
    queryFn: async (): Promise<SupportTicket[]> => {
      const [users, page] = await Promise.all([
        ensureUserIndex(client),
        api.get<Page<TicketItem>>(`support/tickets${toSearchParams({ ...query, limit: '100' })}`),
      ])
      return page.items.map((item) => toSupportTicket(item, users))
    },
  })
}

export function useSupportApps() {
  return useQuery({
    queryKey: qk.support.apps(),
    queryFn: () => api.get<SupportAppRead[]>('support/apps'),
    staleTime: 10 * 60_000,
  })
}

export interface UpdateTicketVariables {
  id: string
  patch: TicketUpdate
}

/**
 * Corrige severidad o tipo, o elige el plazo. El plazo es el de **la tarea**:
 * se invalidan también las tareas y Vencimientos, que muestran la misma fecha.
 */
export function useUpdateSupportTicket() {
  const client = useQueryClient()
  const t = useT()

  return useMutation({
    mutationKey: ['support', 'update'],
    mutationFn: ({ id, patch }: UpdateTicketVariables) =>
      api.patch<TicketItem>(`support/tickets/${id}`, { ...patch }),
    onError: (error) => toast.error(errorMessage(error, t('support.error.save'))),
    onSettled: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: qk.support.all }),
        client.invalidateQueries({ queryKey: qk.tasks.all }),
        client.invalidateQueries({ queryKey: qk.dueSoon.all }),
      ]),
  })
}

/** El panel por usuario, en el orden del servidor: primero quien tiene más abiertos. */
export function useSupportReporters(enabled = true) {
  return useQuery({
    queryKey: qk.support.reporters(),
    enabled,
    queryFn: async (): Promise<SupportReporter[]> => {
      const filas = await api.get<ReporterSummary[]>('support/reporters')
      return filas.map((fila) => ({
        email: fila.reporter_email,
        name: fila.reporter_name,
        apps: fila.apps,
        open: fila.open,
        done: fila.done,
        resolvedByAi: fila.resolved_by_ai,
        lastReportedAt: fila.last_reported_at,
      }))
    },
  })
}

function toSupportLeader(dto: LeaderRead): SupportLeader {
  return {
    companyId: dto.company_id,
    user: { id: dto.user_id, display_name: dto.display_name, email: dto.email },
  }
}

/** Quién recibe, además del responsable, el aviso de cada reporte. */
export function useSupportLeaders() {
  return useQuery({
    queryKey: qk.support.leaders(),
    queryFn: async () => (await api.get<LeaderRead[]>('support/leaders')).map(toSupportLeader),
  })
}

/** Reemplaza la lista de líderes de una empresa. Sólo admin: el backend lo exige. */
export function useSetSupportLeaders() {
  const client = useQueryClient()
  const t = useT()

  return useMutation({
    mutationKey: ['support', 'leaders'],
    mutationFn: (body: LeadersUpdate) => api.put<LeaderRead[]>('support/leaders', { ...body }),
    onSuccess: () => toast.success(t('support.leaders.saved')),
    onError: (error) => toast.error(errorMessage(error, t('support.leaders.error'))),
    onSettled: () => client.invalidateQueries({ queryKey: qk.support.leaders() }),
  })
}

/* ------------------------------------------------------- apps conectadas -- */

/** Las apps con su estado en siete-soporte: si ya se conectaron y qué manual tienen. */
export function useSupportAppStatuses(enabled = true) {
  return useQuery({
    queryKey: qk.support.appStatuses(),
    enabled,
    queryFn: () => api.get<AppStatus[]>('support/apps/status'),
  })
}

/**
 * Una app, consultada cada `pollMs` mientras se espera su primera conexión: es
 * lo que hace que el tour diga "Conectada" solo, sin recargar.
 */
export function useSupportAppStatus(appId: string | null, pollMs?: number) {
  return useQuery({
    queryKey: qk.support.appStatus(appId ?? ''),
    enabled: appId !== null,
    queryFn: () => api.get<AppStatus>(`support/apps/${appId ?? ''}/status`),
    refetchInterval: (query) => (pollMs && !query.state.data?.conectada ? pollMs : false),
  })
}

export function useManualStructure(enabled = true) {
  return useQuery({
    queryKey: qk.support.manualStructure(),
    enabled,
    queryFn: () => api.get<ManualStructure>('support/manual-structure'),
    staleTime: 60 * 60_000,
  })
}

function invalidarApps(client: ReturnType<typeof useQueryClient>) {
  return Promise.all([
    client.invalidateQueries({ queryKey: qk.support.appStatuses() }),
    client.invalidateQueries({ queryKey: qk.support.apps() }),
  ])
}

/** Conecta una app. La respuesta trae la clave: la única vez que se ve. */
export function useConnectSupportApp() {
  const client = useQueryClient()
  const t = useT()

  return useMutation({
    mutationKey: ['support', 'connect'],
    mutationFn: (body: AppConnect) => api.post<AppConnected>('support/apps', { ...body }),
    onError: (error) => toast.error(errorMessage(error, t('support.apps.connect.error'))),
    onSettled: () => invalidarApps(client),
  })
}

/**
 * Carga o reemplaza el manual. Un 422 trae en `details.faltan` lo que le falta:
 * lo muestra el formulario, no un toast.
 */
export function useUploadManual() {
  const client = useQueryClient()

  return useMutation({
    mutationKey: ['support', 'manual'],
    mutationFn: ({ appId, contenido }: { appId: string; contenido: string }) =>
      api.put<AppStatus>(`support/apps/${appId}/manual`, { contenido_md: contenido }),
    onSuccess: (estado) => client.setQueryData(qk.support.appStatus(estado.id), estado),
    onSettled: () => invalidarApps(client),
  })
}

/** Clave nueva; la anterior deja de servir. Sólo admin: el backend lo exige. */
export function useRotateSupportKey() {
  const client = useQueryClient()
  const t = useT()

  return useMutation({
    mutationKey: ['support', 'rotate'],
    mutationFn: (appId: string) => api.post<AppConnected>(`support/apps/${appId}/key`),
    onError: (error) => toast.error(errorMessage(error, t('support.apps.rotate.error'))),
    onSettled: () => invalidarApps(client),
  })
}
